import { useContext, useState } from 'react';

import { Icon as BlueIcon } from '@blueprintjs/core';

import ReactDom from 'react-dom';

import BackIconImage from '@assets/svg/back-icon.svg';

import SearchNotificationImage from '@assets/svg/search-notification.svg';

import {
  Close,
  LoadingImg,
  SearchButton,
} from '@pages/Products/Products.styles';
import Rolling from '@assets/gif/rolling.gif';
import CloseImage from '@assets/svg/close.svg';
import { getFilterRequest } from '@utils';

import { FilterProduct } from '@components';

import { AssetContext } from '@pages/Asset/Asset.query';

import { IProduct } from '@models/Product.model';
import { useMutation, useQueryClient } from 'react-query';
import { getProducts } from '@services/queries/products';
import { IPaginationResult } from '@models/Equipment.model';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { createResource } from '@services/queries/resources';

import {
  ListProductsWithToggle,
  ShowSelectedFields,
} from '@pages/Asset/components/Documents/Components/AddDocuments/components';
import { IProductFilterData } from '@components/Filters/FilterProduct/types';
import {
  Container,
  Overlay,
  Close as CloseStyles,
  Header,
  Title,
  Back,
  Confirm,
  Line,
  Form,
  Divider,
  SpacingDivider,
  Label,
  Input,
  Row,
  CheckBox,
  RowContainer,
  Body,
  Search,
  SearchContainer,
  SearchImg,
} from './AddResource.styles';
import { Associations } from './types';

interface IForm {
  label: string;
  uri: string;
}
/**
 * # Note
 * This should only be called with a manufacturer (and product), else it may create unintended data. See mutationEditResource.mutate
 */
export const AddResource = () => {
  const portalDiv = document.getElementById('portal');

  const { openAddResource, equipmentQuery } = useContext(AssetContext);

  const equipment = equipmentQuery?.data;

  const [associateTo, setAssociateTo] = useState<Associations>('products');

  const [topic, setTopic] = useState<'user' | null>(null);

  const [searchText, setSearchText] = useState('');

  const [data, setData] = useState<IPaginationResult<IProduct> | undefined>(
    equipment?.product
      ? {
          totalPages: 1,
          currentPage: 1,
          results: [equipment.product],
          hasMore: false,
          total: 1,
        }
      : undefined,
  );
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState({
    modelsName: [],
    manufactorNames: [],
  } as IProductFilterData);
  const [dataSelected, setDataSelected] = useState<IProduct[]>(
    equipment?.product ? [equipment.product] : [],
  );
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [alreadySearch, setAlreadySearch] = useState(false);
  const [showErasedSearch, setShowErased] = useState(false);

  const params = useParams<{ equipmentId: string }>();

  const queryClient = useQueryClient();

  const { register, handleSubmit } = useForm<IForm>({
    defaultValues: {
      label: '',
      uri: '',
    },
    reValidateMode: 'onChange',
  });

  const mutationEditResource = useMutation(createResource, {
    onSuccess: () => {
      queryClient.invalidateQueries([
        'equipments',
        params.equipmentId,
        'resources',
      ]);
      openAddResource.set(false);
    },
  });

  const onSubmit = (formData: IForm) => {
    const canSubmit = () => {
      if (associateTo === 'products') {
        if (dataSelected.length === 0) {
          return false;
        }
      }

      return true;
    };

    if (!mutationEditResource.isLoading && canSubmit()) {
      mutationEditResource.mutate({
        data: {
          manufacturerId:
            associateTo === 'manufacturer'
              ? equipment?.product?.manufacturer.id
              : undefined,
          haveOwnerStudio: true,
          label: formData.label,
          uri: formData.uri,
          isGlobal: associateTo === 'global',
          productIds:
            associateTo === 'products'
              ? dataSelected.map(item => item.id)
              : undefined,
        },
      });
    }
  };

  const getProductsWithSearch = useMutation(getProducts, {
    onSuccess: newData => {
      setLoadingSearch(false);
      setShowErased(true);
      setAlreadySearch(true);
      setPage(newData.currentPage);

      if (data && newData.currentPage > 1) {
        const resultsContacted = data.results.concat(newData.results);
        setData({
          total: newData.total,
          currentPage: newData.currentPage,
          totalPages: newData.totalPages,
          hasMore: newData.hasMore,
          results: resultsContacted,
        });
      } else
        setData({
          total: newData.total,
          currentPage: newData.currentPage,
          totalPages: newData.totalPages,
          hasMore: newData.hasMore,
          results: newData.results,
        });
    },
    onError: () => {
      setLoadingSearch(false);
      setShowErased(true);
    },
  });

  const startSearch = (input?: string) => {
    setLoadingSearch(true);

    getProductsWithSearch.mutate({
      pagination: {
        page: 1,
        limit: 20,
        search: input !== undefined ? input : searchText,
      },
      filter: { ...getFilterRequest(filter, true), filterByEquipment: true },
    });
  };

  const loadMore = (input?: string) => {
    if (data?.hasMore)
      getProductsWithSearch.mutate({
        pagination: {
          page: page + 1,
          limit: 20,
          search: input === undefined ? searchText : input,
        },
        filter: { ...getFilterRequest(filter, true), filterByEquipment: true },
      });
  };

  const updateDataSelected = (productsArray?: IProduct[]) => {
    if (!productsArray) return;
    if (productsArray.length === 1) {
      const newProduct = productsArray[0];
      const selected =
        dataSelected.length > 0
          ? dataSelected.find(d => d.id === newProduct.id)
          : undefined;
      if (!selected) {
        setDataSelected(dataSelected.concat([newProduct]));
        return;
      }
      setDataSelected(dataSelected.filter(d => d.id !== newProduct.id));
    } else {
      const productsNotSelected = productsArray.filter(
        product =>
          dataSelected.find(selected => product.id === selected.id) ===
          undefined,
      );
      if (productsNotSelected.length > 0) {
        setDataSelected([...dataSelected, ...productsNotSelected]);
        return;
      }
      setDataSelected([]);
    }
  };

  const searchFunc = (input: string) => {
    setShowErased(false);
    setSearchText(input);
  };

  const onPressEnter = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      startSearch(searchText);
    }
  };

  return openAddResource.get() && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              {topic && (
                <>
                  <Back
                    src={BackIconImage}
                    alt="back"
                    onClick={() => setTopic(null)}
                  />
                  <SpacingDivider />
                </>
              )}
              <Title>Add Resource</Title>
              <CloseStyles onClick={() => openAddResource.set(false)}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </CloseStyles>
            </Header>
            <Body>
              <Divider height="24px" />
              <form onSubmit={handleSubmit(onSubmit)} id="addResourceForm">
                <Label>Resource Name</Label>
                <Divider height="8px" />
                <Input
                  placeholder="Ex: Internal Ticketing System"
                  {...register('label')}
                  required
                  autoFocus
                />
                <Divider height="16px" />

                <Label>Resource URL</Label>
                <Divider height="8px" />
                <Input
                  type="url"
                  placeholder="https://resource.com"
                  {...register('uri')}
                  required
                />
                <Divider height="16px" />

                <Label className="heavy">Associate to</Label>
                <Divider height="8px" />
                <Row>
                  <Row className="innerRow">
                    <CheckBox
                      onClick={() => setAssociateTo('products')}
                      checked={associateTo === 'products'}
                      type="button"
                    />
                    <Label className="light">Products</Label>
                  </Row>

                  <Row className="innerRow">
                    <CheckBox
                      onClick={() => setAssociateTo('manufacturer')}
                      checked={associateTo === 'manufacturer'}
                      type="button"
                    />
                    <Label className="light">Manufacturer</Label>
                  </Row>

                  <Row className="innerRow">
                    <CheckBox
                      onClick={() => setAssociateTo('global')}
                      checked={associateTo === 'global'}
                      type="button"
                    />
                    <Label className="light">Global</Label>
                  </Row>
                </Row>

                <Divider height="8px" />

                {associateTo === 'global' && (
                  <Label className="description">
                    This resource will be available for any product or
                    manufacturer.
                  </Label>
                )}

                {associateTo === 'manufacturer' && (
                  <Label className="description">
                    {`This resource will be visible on assets related to ${equipment?.product?.manufacturer.name} products.`}
                  </Label>
                )}

                {associateTo === 'products' && (
                  <>
                    <div
                      style={{
                        flexDirection: 'row',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <RowContainer>
                        <SearchContainer>
                          <SearchImg
                            src={SearchNotificationImage}
                            alt="search"
                          />
                          <Search
                            value={searchText}
                            placeholder="Search Products"
                            onChange={e => searchFunc(e.target.value)}
                            onKeyDown={onPressEnter}
                          />
                          {loadingSearch && (
                            <LoadingImg src={Rolling} alt="loading" />
                          )}
                          {showErasedSearch &&
                            searchText.length > 0 &&
                            !loadingSearch && (
                              <Close
                                src={CloseImage}
                                alt="close"
                                onClick={async () => {
                                  await searchFunc('');
                                  startSearch('');
                                }}
                              />
                            )}
                          {!loadingSearch &&
                            !showErasedSearch &&
                            (searchText.length > 0 || alreadySearch) && (
                              <SearchButton onClick={() => startSearch()}>
                                Search
                              </SearchButton>
                            )}
                        </SearchContainer>
                        <FilterProduct
                          filterData={filter}
                          setFilterData={setFilter}
                          startSearch={startSearch}
                        />
                      </RowContainer>
                      {dataSelected.length > 0 && (
                        <ShowSelectedFields
                          listOfProducts={dataSelected}
                          setListOfProducts={setDataSelected}
                        />
                      )}
                    </div>

                    <ListProductsWithToggle
                      data={data?.results}
                      total={data?.total}
                      dataSelected={dataSelected}
                      loadMore={() => loadMore()}
                      hasMore={data?.hasMore}
                      isLoading={loadingSearch}
                      updateDataSelected={updateDataSelected}
                    />
                  </>
                )}
              </form>
            </Body>
            <Divider height="24px" />
            <Form>
              <Line>
                <Confirm type="submit" form="addResourceForm">
                  Add
                </Confirm>
              </Line>
            </Form>
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
